import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { LuArrowLeft } from "react-icons/lu";
import { Loader, Spinner } from "../../components/ui/Loader";
import { ProjectsGrid } from "../../components/shared/ProjectsGrid";
import {
  fetchPartner,
  fetchPartnerProjects,
  updatePartner,
} from "../../lib/admin";
import { isValidPhone } from "../../lib/profiles";
import { PartnerProfile } from "../../models/account";
import { Project } from "../../models/project";

type PartnerWithTelegram = PartnerProfile & { telegram_username: string | null };

export const AdminPartnerCard = () => {
  const { id } = useParams<{ id: string }>();
  const [partner, setPartner] = useState<PartnerWithTelegram | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!id) return;
    Promise.all([fetchPartner(id), fetchPartnerProjects(id)])
      .then(([p, pr]) => {
        if (!p) setError("Партнера не знайдено");
        setPartner(p);
        setProjects(pr);
        setName(p?.name ?? "");
        setCompany(p?.company ?? "");
        setPhone(p?.phone ?? "");
        setCity(p?.city ?? "");
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Помилка завантаження"))
      .finally(() => setLoading(false));
  }, [id]);

  const handleSave = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!id || !partner) return;
    setError(null);
    setSaved(false);
    if (!name.trim()) {
      setError("Вкажіть імʼя");
      return;
    }
    if (!isValidPhone(phone)) {
      setError("Вкажіть коректний номер телефону");
      return;
    }
    setSaving(true);
    try {
      const updated = await updatePartner(id, {
        name: name.trim(),
        company: company.trim() || null,
        phone: phone.trim(),
        city: city.trim() || null,
      });
      setPartner({ ...updated, telegram_username: partner.telegram_username });
      setSaved(true);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Помилка збереження");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="mx-auto w-full max-w-cabinet flex-1 px-5 py-8 md:px-8">
      <Link to="/admin/partners" className="link-brand gap-1">
        <LuArrowLeft aria-hidden />
        Партнери
      </Link>

        {loading ? (
          <Loader />
        ) : error && !partner ? (
          <p className="mt-6 rounded border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
            {error}
          </p>
        ) : partner ? (
          <div className="mt-6 items-start grid grid-cols-1 gap-6 lg:grid-cols-2">
            <section className="card p-4 sm:p-6">
              <div className="flex items-center justify-between gap-4">
                <h1 className="text-xl font-bold text-white">Профіль партнера</h1>
                {!editing && (
                  <button
                    type="button"
                    onClick={() => setEditing(true)}
                    className="text-sm font-medium text-brand transition hover:opacity-80"
                  >
                    Редагувати
                  </button>
                )}
              </div>
              <p className="mt-1 text-sm text-neutral-500">
                {partner.email ? (
                  <a href={`mailto:${partner.email}`} className="hover:underline">
                    {partner.email}
                  </a>
                ) : (
                  "—"
                )}
              </p>
              <form onSubmit={handleSave} className="mt-3 sm:mt-5 flex flex-col gap-2 sm:gap-4">
                <label className="block">
                  <span className="mb-2 block text-sm font-semibold text-neutral-300">
                    Імʼя
                  </span>
                  {editing ? (
                    <input
                      className="input-dark"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  ) : (
                    <span className="block text-sm text-white">{partner.name ?? "—"}</span>
                  )}
                </label>
                <label className="block">
                  <span className="mb-2 block text-sm font-semibold text-neutral-300">
                    Компанія
                  </span>
                  {editing ? (
                    <input
                      className="input-dark"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                    />
                  ) : (
                    <span className="block text-sm text-white">{partner.company ?? "—"}</span>
                  )}
                </label>
                <label className="block">
                  <span className="mb-2 block text-sm font-semibold text-neutral-300">
                    Телефон
                  </span>
                  {editing ? (
                    <input
                      className="input-dark"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                    />
                  ) : partner.phone ? (
                    <a href={`tel:${partner.phone}`} className="block text-sm text-white hover:underline">
                      {partner.phone}
                    </a>
                  ) : (
                    <span className="block text-sm text-white">—</span>
                  )}
                </label>
                <label className="block">
                  <span className="mb-2 block text-sm font-semibold text-neutral-300">
                    Telegram
                  </span>
                  {partner.telegram_username ? (
                    <a
                      href={`https://t.me/${partner.telegram_username}`}
                      target="_blank"
                      rel="noreferrer"
                      className="link-brand text-sm"
                    >
                      @{partner.telegram_username}
                    </a>
                  ) : (
                    <span className="block text-sm text-white">—</span>
                  )}
                </label>
                <label className="block">
                  <span className="mb-2 block text-sm font-semibold text-neutral-300">
                    Місто
                  </span>
                  {editing ? (
                    <input
                      className="input-dark"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                    />
                  ) : (
                    <span className="block text-sm text-white">{partner.city ?? "—"}</span>
                  )}
                </label>
                {editing && error && (
                  <p className="rounded border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
                    {error}
                  </p>
                )}
                {!editing && saved && (
                  <p className="rounded border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-400">
                    Збережено
                  </p>
                )}
                {editing && (
                  <button
                    type="submit"
                    disabled={saving}
                    className="mt-3 btn-brand w-full justify-center disabled:opacity-70"
                  >
                    {saving ? (
                      <>
                        <Spinner className="h-4 w-4" />
                        Зачекайте…
                      </>
                    ) : (
                      "Зберегти"
                    )}
                  </button>
                )}
              </form>
            </section>

            <section className="card p-4 sm:p-6">
              <h2 className="text-xl font-bold text-white">Проєкти партнера</h2>
              {projects.length === 0 ? (
                <p className="mt-4 text-sm text-neutral-400">Проєктів немає</p>
              ) : (
                <div className="mt-4">
                  <ProjectsGrid projects={projects} hrefBase="/admin/projects" showDetails={false}/>
                </div>
              )}
            </section>
          </div>
        ) : null}
    </main>
  );
};
